import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Trophy,
  Facebook,
  Twitter,
  Instagram,
  Youtube,
  Mail,
  Phone,
  MapPin,
  CheckCircle
} from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { lang } = useParams();
  const { t, i18n } = useTranslation('common');

  // Get current language from URL or i18n
  const currentLang = lang || i18n.language || 'en';

  const socialLinks = [
    { icon: Facebook, url: '#', label: 'Facebook' },
    { icon: Twitter, url: '#', label: 'Twitter' },
    { icon: Instagram, url: '#', label: 'Instagram' },
    { icon: Youtube, url: '#', label: 'YouTube' },
  ];

  const quickLinks = [
    { label: t('footer.quickLinks.aboutUs'), path: `/${currentLang}/about` },
    { label: t('footer.quickLinks.awardCategories'), path: `/${currentLang}/nominations#categories` },
    { label: t('footer.quickLinks.howToApply'), path: `/${currentLang}/nominations` },
    { label: t('footer.quickLinks.pastWinners'), path: `/${currentLang}/winners` },
  ];

  const categoriesColumn1 = [
    t('footer.categories.bestCropInnovation'),
    t('footer.categories.sustainableFarming'),
    t('footer.categories.agTechStartup'),
    t('footer.categories.womenInAgriculture'),
    t('footer.categories.youngFarmerAward'),
    t('footer.categories.digitalTransformation'),
  ];

  const categoriesColumn2 = [
    t('footer.categories.organicFarming'),
    t('footer.categories.waterConservation'),
    t('footer.categories.soilHealth'),
    t('footer.categories.livestockExcellence'),
    t('footer.categories.communityLeadership'),
    t('footer.categories.exportExcellence'),
  ];

  return (
    <Box component="footer" sx={{ backgroundColor: '#000000', color: 'white' }}>
      <Container maxWidth="xl" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {/* Brand Section */}
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Trophy size={40} style={{ color: '#D4A747', marginRight: 12 }} />
                <Box>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {t('footer.brandName')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#D4A747',
                      textTransform: 'uppercase',
                      letterSpacing: 1.2
                    }}
                  >
                    {t('footer.brandSubtitle')}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
                {t('footer.brandDescription')}
              </Typography>
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, mb: 2 }}>
                  {t('footer.followUs')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {socialLinks.map((social, index) => {
                    const Icon = social.icon;
                    return (
                      <motion.div
                        key={index}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <IconButton
                          component="a"
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={social.label}
                          sx={{
                            backgroundColor: 'rgba(212, 167, 71, 0.1)',
                            color: '#D4A747',
                            border: '1px solid rgba(212, 167, 71, 0.3)',
                            '&:hover': {
                              backgroundColor: '#D4A747',
                              color: 'black',
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          <Icon size={20} />
                        </IconButton>
                      </motion.div>
                    );
                  })}
                </Box>
              </Box>
            </motion.div>
          </Grid>

          {/* Quick Links */}
          <Grid size={{ xs: 12, md: 6, lg: 2 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, mb: 2 }}>
                {t('footer.quickLinks.title')}
              </Typography>
              <List dense sx={{ py: 0 }}>
                {quickLinks.map((link) => (
                  <ListItem
                    key={link.path}
                    component={Link}
                    to={link.path}
                    sx={{
                      px: 0,
                      py: 0.5,
                      color: 'rgba(255, 255, 255, 0.7)',
                      textDecoration: 'none',
                      '&:hover': {
                        color: '#D4A747',
                      },
                      transition: 'color 0.3s ease',
                    }}
                  >
                    <ListItemText primary={link.label} />
                  </ListItem>
                ))}
              </List>
            </motion.div>
          </Grid>

          {/* Award Categories */}
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, mb: 2 }}>
                {t('footer.categories.title')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* Column 1 */}
                <List dense sx={{ py: 0, flex: 1 }}>
                  {categoriesColumn1.map((category) => (
                    <ListItem
                      key={category}
                      component={Link}
                      to={`/${currentLang}/nominations#categories`}
                      sx={{
                        px: 0,
                        py: 0.5,
                        textDecoration: 'none',
                        '&:hover .MuiListItemText-primary': {
                          color: '#D4A747',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        <CheckCircle size={14} style={{ color: '#D4A747' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={category}
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: {
                            color: 'rgba(255, 255, 255, 0.7)',
                            transition: 'color 0.3s ease',
                            fontSize: '0.8rem',
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
                {/* Column 2 */}
                <List dense sx={{ py: 0, flex: 1 }}>
                  {categoriesColumn2.map((category) => (
                    <ListItem
                      key={category}
                      component={Link}
                      to={`/${currentLang}/nominations#categories`}
                      sx={{
                        px: 0,
                        py: 0.5,
                        textDecoration: 'none',
                        '&:hover .MuiListItemText-primary': {
                          color: '#D4A747',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        <CheckCircle size={14} style={{ color: '#D4A747' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={category}
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: {
                            color: 'rgba(255, 255, 255, 0.7)',
                            transition: 'color 0.3s ease',
                            fontSize: '0.8rem',
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </motion.div>
          </Grid>

          {/* Newsletter & Contact */}
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              {/* <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, mb: 2 }}>
                {t('footer.newsletter.title')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                {t('footer.newsletter.description')}
              </Typography>
              <Box component="form" sx={{ mb: 3 }}>
                <TextField
                  type="email"
                  placeholder={t('footer.newsletter.placeholder')}
                  fullWidth
                  size="small"
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(212, 167, 71, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#D4A747',
                      },
                    },
                    '& .MuiOutlinedInput-input::placeholder': {
                      color: 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  endIcon={<ArrowRight size={18} />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                    color: 'black',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                    }
                  }}
                >
                  {t('footer.newsletter.subscribe')}
                </Button>
              </Box> */}

              <Box>
                <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, mb: 2 }}>
                  {t('footer.contact.title')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Mail size={18} style={{ color: '#D4A747', marginRight: 8 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {t('footer.contact.email')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Phone size={18} style={{ color: '#D4A747', marginRight: 8 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {t('footer.contact.phone')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <MapPin size={38} style={{ color: '#D4A747', marginRight: 8, marginTop: 2 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {t('footer.contact.address')}
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>

        {/* Bottom Bar */}
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 4 }} />
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2
        }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: { xs: 'center', md: 'left' } }}>
            © {currentYear} {t('footer.copyright')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 3 }, alignItems: 'center' }}>
            {[
              { label: t('footer.legal.privacyPolicy'), path: `/${currentLang}/privacy-policy` },
              { label: t('footer.legal.termsConditions'), path: `/${currentLang}/terms-and-conditions` },
            ].map((link) => (
              <Typography
                key={link.path}
                component={Link}
                to={link.path}
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textDecoration: 'none',
                  '&:hover': {
                    color: '#D4A747',
                  },
                  transition: 'color 0.3s ease',
                }}
              >
                {link.label}
              </Typography>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;